import { components } from "@/api-schema";
import { useCustomerSearchQuery } from "@/common/api/customer/use-customer-search.query";
import { sortByLabel } from "@/common/helpers/sort-by-label";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { getCustomerDisplayName } from "@/components/data-table/predefined/customer-table/_helpers/get-customer-display-name";
import { BaseFilterProps, Filter, FilterData, FilterPaginationState } from "../_types";
import { FilterComponent } from "../base-filter";

type Customer = components["schemas"]["Customer"];
export type QueryBody = components["schemas"]["CustomerSearchBody"];

const FILTER_PAGE_SIZE = 50;

export class CustomerFilter extends Filter<QueryBody, unknown> {
  readonly id: string = "customers";
  readonly labelSingular = "common:Customer";
  readonly labelPlural = "common:Customers";
  readonly placeholder = "common:SelectCustomers";
  readonly searchPlaceholder = "common:SearchCustomers";
  readonly emptyText = "common:NoCustomersFound";

  constructor({ id, body = {} }: { id?: string; body?: Partial<QueryBody> } = {}) {
    super();
    if (id) this.id = id;
    this.body = body as QueryBody;
  }

  getData(): FilterData {
    return { options: [], isLoading: false, error: null };
  }

  render(props: BaseFilterProps) {
    return <CustomerFilterComponent filter={this} {...props} />;
  }
}

function CustomerFilterComponent({ filter, ...props }: { filter: CustomerFilter } & BaseFilterProps) {
  const { data, pagination } = useCustomerFilterData(filter.body);

  const filterWithData = Object.create(filter);
  filterWithData.getData = () => data;

  return <FilterComponent filter={filterWithData} pagination={pagination} {...props} />;
}

function useCustomerFilterData(body: QueryBody): { data: FilterData; pagination: FilterPaginationState } {
  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination({
    initialPageSize: FILTER_PAGE_SIZE,
  });

  const {
    data: customers,
    isPending: isLoading,
    error,
  } = useCustomerSearchQuery({
    options: {
      parameters: { pageSize, search: debouncedSearchTerm || undefined },
      body,
    },
  });

  const options = buildCustomerFilterOptions(customers?.data);

  const loadedCount = customers?.data?.length ?? 0;
  const total = customers?.meta?.total ?? 0;

  const pagination = createPaginationState(total, loadedCount, isLoading);

  return {
    data: { options, isLoading, error },
    pagination,
  };
}

export function buildCustomerFilterOptions(customers?: Customer[]): FilterData["options"] {
  if (!customers?.length) {
    return [];
  }

  const options: FilterData["options"] = [];
  const seenIds = new Set<string>();

  for (const customer of customers) {
    const id = customer.uuid?.trim();
    const name = getCustomerDisplayName(customer).trim();

    if (!id || !name || seenIds.has(id)) {
      continue;
    }

    seenIds.add(id);
    options.push({ id, name });
  }

  return sortByLabel(options, (option) => option.name);
}
